import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { SelectComponent } from "@/components/select/Select";
import { TextArea } from "@/components/text-area/TextArea";
import { AppColors } from "@/styles/colors";
import { Card, Field, Flex, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { cashRequestCreationSchema } from "../schema/cash-requests.schema";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppRoutes } from "@/constants/routes";
import { useNavigate } from "react-router-dom";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { createRequestHandler } from "./handler-functions";
import { getSucursales, Sucursal } from "@/services/accounts.service";
import {
  getOptionsHandler,
  loadOptionsHandler,
} from "../schema/handler-functions";
import { AutocompleteSelect } from "@/components/select/AutocompleteSelect";
import { getUsuariosMobileByCuenta } from "@/services/users.service";
import { useAuth } from "@/hooks/useAuth";
import { formatCurrencyInputToNumber, isEmpty } from "@/utils/query-string";
import { useRoles } from "@/hooks/useRoles";

export const CashRequestCreationPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  const [loadingSucursales, setLoadingSucursales] = useState<boolean>(false);

  const [sucursales, setSucursales] = useState<Sucursal[] | null>([]);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchSucursales = async () => {
    setLoadingSucursales(true);

    const response = await getSucursales({
      usuario: user?.localAccountId ?? "",
    });
    setSucursales(response);

    setLoadingSucursales(false);
  };

  const navigate = useNavigate();
  const {
    register,
    control,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
  } = useForm({
    resolver: zodResolver(cashRequestCreationSchema),
  });

  const { user } = useAuth();

  const [loadingCreateCashRequest, setLoadingCreateCashRequest] =
    useState<boolean>(false);

  const { fetchPermissions } = useRoles()

  const handleCreateCashRequest = async () => {
    await createRequestHandler({
      navigate,
      setLoadingCreateCashRequest,
      watchedValues,
      user: user?.name ?? "",
      fetchPermissions,
    });
  };
  const watchedValues = useWatch({ control });

  const hasNoFilledValues = useMemo(() => {
    const { sucursal, account, request_amount } = watchedValues || {};

    return (
      isEmpty(sucursal) ||
      isEmpty(account) ||
      isEmpty(request_amount) ||
      formatCurrencyInputToNumber(watchedValues?.request_amount) < 1
    );
  }, [watchedValues]);

  useEffect(() => {
    clearErrors("request_amount");

    if (!isEmpty(watchedValues?.request_amount)) {
      const monto = formatCurrencyInputToNumber(watchedValues?.request_amount);

      if (monto < 1) {
        setError("request_amount", {
          message: "El valor debe ser mayor o igual a $1.",
        });
      } else {
        clearErrors("request_amount");
      }
    }
  }, [watchedValues?.request_amount, errors]);

  const handleCancel = () => {
    navigate(AppRoutes.CASH_REQUESTS);
  };

  const [loadingPersons, setLoadingPersons] = useState<boolean>(false);

  const [cuentaUsuarios, setCuentaUsuarios] = useState<any>([]);

  const [loadingCuentaUsuarios, setLoadingCuentaUsuarios] =
    useState<boolean>(false);

  const getUsuariosByCuenta = async () => {
    setLoadingCuentaUsuarios(true);
    const response = await getUsuariosMobileByCuenta({
      cuenta: watchedValues?.account?.value,
    });

    setCuentaUsuarios(response);
    setLoadingCuentaUsuarios(false);
  };

  const getOptions = async (
    inputValue: string,
  ): Promise<
    {
      label: string;
      value: string;
      tipoCuenta: string;
      idTipoCuenta: number;
    }[]
  > => {
    return getOptionsHandler({
      inputValue,
    });
  };

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadOptions = (inputValue: string): Promise<Option[]> => {
    return loadOptionsHandler({
      getOptions,
      inputValue,
      setLoadingPersons,
      timeoutRef,
    });
  };

  useEffect(() => {
    if (isActive) {
      fetchSucursales();
    }
  }, [isActive]);

  const watchedAccount = useWatch({ control, name: "account" });

  useEffect(() => {
    if (watchedAccount) {
      setValue("usuarioMobile", []);
      getUsuariosByCuenta();
    }
  }, [watchedAccount]);

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Agregar solicitud de efectivo"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleCreateCashRequest}
        disableAdd={hasNoFilledValues}
        loading={loadingCreateCashRequest}
      />
      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Flex gap={"50px"}>
            <Field.Root invalid={!!errors?.account?.message} gap="12px">
              <Field.Label>
                {" "}
                <Text fontSize="16px">Cuenta</Text>
              </Field.Label>

              <AsyncAutocompleteSelect
                control={control}
                loadOptions={loadOptions}
                name="account"
                placeholder="Buscar..."
                isLoading={loadingPersons}
              />

              <Field.ErrorText>
                {String(errors?.account?.message)}
              </Field.ErrorText>
            </Field.Root>

            <Field.Root invalid={!!errors?.sucursal?.message}>
              <Field.Label>
                <Text fontSize="16px">Sucursal</Text>
              </Field.Label>
              <SelectComponent
                fullWidth
                items={
                  sucursales?.map((sucursal) => ({
                    label: sucursal?.nombre,
                    value: sucursal?.clave,
                  })) ?? []
                }
                menuMaxHeight={{
                  xs: "350px",
                  sm: "420px",
                  md: "500px",
                  lg: "600px",
                  xl: "1000px",
                }}
                name="sucursal"
                control={control}
                closeOnSelect
                clearable
                size="xs"
                minHeight="40px"
                placeholder="Seleccionar sucursal"
                loading={loadingSucursales}
                disabled={loadingSucursales}
              />
              <Field.ErrorText>
                {String(errors?.sucursal?.message)}
              </Field.ErrorText>
            </Field.Root>

            <Field.Root invalid={!!errors?.request_amount?.message} gap="12px">
              <Field.Label>
                <Text fontSize="16px">Monto</Text>
              </Field.Label>
              <InputCurrency
                control={control}
                name="request_amount"
                height="40px"
                maxLength={20}
              />

              <Field.ErrorText>
                {String(errors?.request_amount?.message)}
              </Field.ErrorText>
            </Field.Root>

            <Field.Root invalid={!!errors?.usuarioMobile?.message} gap="12px">
              <Field.Label>
                <Text>Usuario</Text>
              </Field.Label>
              <AutocompleteSelect
                control={control}
                inputName="usuarioMobile"
                loading={loadingCuentaUsuarios}
                disabled={!watchedValues?.account || loadingCuentaUsuarios}
                multiple={false}
                items={
                  cuentaUsuarios?.map((cu: any) => ({
                    label: `${cu?.nombre} ${cu?.apellido}`,
                    value: cu?.id,
                  })) ?? []
                }
              />
            </Field.Root>
          </Flex>
        </Card.Body>
      </Card.Root>
      <Flex flexDir="column" gap="12px">
        <Text fontWeight={700} fontSize="20px">
          Detalle
        </Text>
        <TextArea
          errors={errors}
          inputName="request_detail"
          maxLength={50}
          register={register}
          placeholder="Ingresar una descripción de la solicitud"
          helperText="Máximo 50 caracteres"
        />
      </Flex>
    </Flex>
  );
};
